import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isAdminUser } from '../../utils/userUtils';
import CollapsibleMenuItem from './CollapsibleMenuItem';
import MenuConfigService from '../../services/menuConfigService';
import { getWorkingRoute } from '../../utils/routeMapping';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
// BrandSidebar removed - no longer used
import {
  POSIcon,
  DashboardIcon,
  OrderIcon,
  MenuIcon,
  CustomerIcon,
  ReportIcon,
  PaymentIcon,
  StaffIcon,
  NotificationIcon,
  IntegrationIcon,
  SettingsIcon
} from './PosIcons';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ClockIcon,
  CalendarDaysIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

// Use public icons (dark/light) placed in public/icons
const sidebarLogoLight = process.env.PUBLIC_URL + '/icons/sp-logo-blue.svg';
const sidebarLogoDark = process.env.PUBLIC_URL + '/icons/sp-logo-white.svg';

const Sidebar = ({ onToggle, sidebarOpen, setSidebarOpen, darkMode, isCollapsed = false, onCollapse }) => {
  const location = useLocation();
  const { 
    isAuthenticated, 
    currentUser: user, 
    currentUserRole: userRole,
    selectedBusiness,
    sidebarType: contextSidebarType
  } = useAuthBusiness();

  const [dynamicAdminMenuItems, setDynamicAdminMenuItems] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(isCollapsed);
  const [currentTime, setCurrentTime] = React.useState(new Date());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const [expandedGroups, setExpandedGroups] = React.useState({});
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Update internal state when props change
  React.useEffect(() => {
    setCollapsed(isCollapsed);
  }, [isCollapsed]);

  // Initialize MenuConfigService event listeners
  React.useEffect(() => {
    MenuConfigService.initializeEventListeners();
  }, []);

  // Listen for admin menu configuration changes
  React.useEffect(() => {
    const loadAdminMenuConfig = async () => {
      console.log('🔄 Loading admin menu config...');

      // First try Firebase, then fallback to localStorage
      let adminConfig = await MenuConfigService.loadAdminSidebarConfigFromFirebase();

      if (!adminConfig || adminConfig.length === 0) {
        console.log('📁 Falling back to localStorage admin config');
        adminConfig = MenuConfigService.getAdminSidebarConfig();
      }

      if (adminConfig && adminConfig.length > 0) {
        console.log('🎉 Admin config loaded:', adminConfig);
        // Convert to expected format for rendering
        const convertedConfig = adminConfig
          .filter(item => item.visible !== false) // Only include visible items
          .map(item => ({
            type: item.type,
            path: item.path,
            label: item.label,
            icon: item.icon ? MenuConfigService.renderIcon(item.icon, 'w-5 h-5') : null,
            children: item.children ? item.children
              .filter(child => child.visible !== false) // Only include visible children
              .map(child => ({
                path: child.path,
                icon: MenuConfigService.renderIcon(child.icon, 'h-4 w-4'),
                label: child.label
              })) : undefined
          }));
        setDynamicAdminMenuItems(convertedConfig);
        console.log('✅ Admin menu updated with', convertedConfig.length, 'items');
      } else {
        console.log('⚠️ No admin config found, using static menu');
        setDynamicAdminMenuItems(adminMenuItems); // Fallback to static
      }
    };

    // Load initial config
    loadAdminMenuConfig();

    // Listen for configuration changes
    const unsubscribe = MenuConfigService.onConfigChange((data) => {
      console.log('Sidebar: Admin menu configuration changed, reloading...', data);
      loadAdminMenuConfig();
    });

    // Listen for admin-specific configuration changes
    const handleAdminConfigChange = async (event) => {
      console.log('Sidebar: Admin config changed, reloading from Firebase...', event.detail);
      await loadAdminMenuConfig();
    };

    // Listen for all sidebar configuration changes
    const handleAllSidebarsConfigChange = (event) => {
      console.log('Sidebar: All sidebars config changed:', event.detail);
      loadAdminMenuConfig();
    };

    window.addEventListener('adminSidebarConfigChanged', handleAdminConfigChange);
    window.addEventListener('sidebarConfigChanged', handleAdminConfigChange); // General reload trigger
    window.addEventListener('allSidebarsConfigChanged', handleAllSidebarsConfigChange);

    // Also listen for localStorage changes (cross-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'adminSidebarConfig') {
        loadAdminMenuConfig();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminSidebarConfigChanged', handleAdminConfigChange);
      window.removeEventListener('sidebarConfigChanged', handleAdminConfigChange);
      window.removeEventListener('allSidebarsConfigChanged', handleAllSidebarsConfigChange);
    };
  }, []);

  // Filter menu items based on user's access panels - No longer used with restaurant manager menu removal
  // const filterMenuByAccess = (menuItems, accessPanels) => { ... }

  // useState hooks for component state
  // darkMode comes from props, no need to redefine

  // Admin menüsü - Full sistem kontrolü
  const adminMenuItems = [
    {
      type: 'single',
      path: '/dashboard/admin/',
      icon: <DashboardIcon className="w-5 h-5" />,
      label: 'Admin Dashboard'
    },
    // Kullanıcı Yönetimi
    {
      type: 'group',
      label: 'Kullanıcı Merkezi',
      variant: 'v3',
      icon: <CustomerIcon className="w-5 h-5" />,
      children: [
        { path: '/dashboard/admin/users', icon: <CustomerIcon className="h-4 w-4" />, label: 'Müşteriler' },
        { path: '/dashboard/admin/system-users', icon: <StaffIcon className="h-4 w-4" />, label: 'Personel' },
        { path: '/dashboard/admin/roles', icon: <ShieldCheckIcon className="h-4 w-4" />, label: 'Roller' },
        { path: '/dashboard/admin/sectors', icon: <MenuIcon className="h-4 w-4" />, label: 'Sektörler' }
      ]
    },
    // Sistem Yönetimi
    {
      type: 'group',
      label: 'Sistem Sağlığı',
      variant: 'v3',
      icon: <SettingsIcon className="w-5 h-5" />,
      children: [
        { path: '/dashboard/admin/reports', icon: <ReportIcon className="h-4 w-4" />, label: 'Raporlar' },
        { path: '/dashboard/admin/logs', icon: <NotificationIcon className="h-4 w-4" />, label: 'Loglar' },
        { path: '/dashboard/admin/backup', icon: <SettingsIcon className="h-4 w-4" />, label: 'Yedekleme' },
        { path: '/dashboard/admin/settings', icon: <SettingsIcon className="h-4 w-4" />, label: 'Ayarlar' }
      ]
    },
    // İşletme ve Abonelik
    {
      type: 'group',
      label: 'İşletme Yönetimi',
      variant: 'v3',
      icon: <PaymentIcon className="w-5 h-5" />,
      children: [
        { path: '/dashboard/admin/subscription', icon: <PaymentIcon className="h-4 w-4" />, label: 'Abonelik' },
        { path: '/dashboard/admin/billing', icon: <PaymentIcon className="h-4 w-4" />, label: 'Faturalar' },
        { path: '/dashboard/admin/support', icon: <CustomerIcon className="h-4 w-4" />, label: 'Destek' },
        { path: '/dashboard/admin/performance', icon: <ReportIcon className="h-4 w-4" />, label: 'Performans' }
      ]
    },
    // Araçlar
    {
      type: 'group',
      label: 'Araçlar & Güvenlik',
      variant: 'v3',
      icon: <IntegrationIcon className="w-5 h-5" />,
      children: [
        { path: '/dashboard/admin/sidebar-manager', icon: <MenuIcon className="h-4 w-4" />, label: 'Sidebar' },
        { path: '/dashboard/admin/file-manager', icon: <MenuIcon className="h-4 w-4" />, label: 'Dosyalar' },
        { path: '/dashboard/admin/security', icon: <SettingsIcon className="h-4 w-4" />, label: 'Güvenlik' },
        { path: '/dashboard/admin/api-requests', icon: <IntegrationIcon className="h-4 w-4" />, label: 'API' }
      ]
    },
    // WhatsApp
    {
      type: 'group',
      label: 'WhatsApp AI',
      variant: 'v3',
      icon: <IntegrationIcon className="w-5 h-5" />,
      children: [
        { path: '/dashboard/admin/whatsapp', icon: <IntegrationIcon className="h-4 w-4" />, label: 'Panel' },
        { path: '/dashboard/admin/whatsapp/integrations', icon: <SettingsIcon className="h-4 w-4" />, label: 'Kurulum' },
        { path: '/dashboard/admin/whatsapp/conversations', icon: <CustomerIcon className="h-4 w-4" />, label: 'Mesajlar' },
        { path: '/dashboard/admin/whatsapp/analytics', icon: <ReportIcon className="h-4 w-4" />, label: 'Analitik' }
      ]
    },
    // Kurumsal E-Posta
    {
      path: '/dashboard/admin/email-center',
      icon: <EnvelopeIcon className="h-5 w-5" />,
      label: 'E-Posta Merkezi'
    }
  ];


  // Business Owner menüsü - Multi-brand yönetimi
  const businessOwnerMenuItems = [
    {
      type: 'single',
      path: '/dashboard/personal',
      icon: <DashboardIcon className="w-5 h-5" />,
      label: 'Kişisel Dashboard'
    },

    // İşletme Yönetimi
    {
      type: 'single',
      path: '/brands',
      label: 'İşletmelerim',
      icon: <MenuIcon className="w-5 h-5" />
    },

    // Birleşik Abonelik Yönetimi
    {
      type: 'single',
      path: '/subscription/unified',
      icon: <PaymentIcon className="w-5 h-5" />,
      label: 'Abonelik Yönetimi'
    },

    // ...Kullanıcı Yönetimi kaldırıldı...

    // Kullanıcı Yönetimi
    {
      type: 'single',
      path: '/business/user-management-dashboard',
      icon: <CustomerIcon className="w-5 h-5" />,
      label: 'Kullanıcı Yönetimi'
    },
    // Gelişmiş Analitikler
    {
      type: 'single',
      path: '/business/analytics-dashboard',
      icon: <ReportIcon className="w-5 h-5" />,
      label: 'Gelişmiş Analitikler'
    },
    // Ayarlar
    {
      type: 'single',
      path: '/business/settings-dashboard',
      icon: <SettingsIcon className="w-5 h-5" />,
      label: 'Ayarlar'
    }
  ];

  // Get dynamic dashboard path based on business type
  const getDashboardPath = () => {
    if (selectedBusiness && selectedBusiness.business_type) {
      switch (selectedBusiness.business_type) {
        case 'restaurant':
          return '/sectors/restaurant/pos/dashboard';
        case 'retail':
          return '/sectors/retail/dashboard';
        case 'service':
          return '/sectors/service/dashboard';
        default:
          return '/sectors/restaurant/pos/dashboard';
      }
    }
    return '/sectors/restaurant/pos/dashboard';
  };

  // Restaurant Manager menüsü - Removed: now use BusinessSidebar instead

  // Employee/Personel menüsü - Sınırlı erişim
  const employeeMenuItems = [
    {
      type: 'single',
      path: getDashboardPath(),
      icon: (
        <div className="bg-gradient-to-tl from-orange-600 to-red-400 shadow-soft-2xl mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5">
          <DashboardIcon className="fill-white h-3 w-3" />
        </div>
      ),
      label: 'Dashboard'
    },
    {
      type: 'single',
      path: '/sectors/restaurant/pos/quick-sales',
      icon: (
        <div className="bg-gradient-to-tl from-green-600 to-emerald-400 shadow-soft-2xl mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5">
          <POSIcon className="fill-white h-3 w-3" />
        </div>
      ),
      label: 'POS & Satış'
    },
    {
      type: 'single',
      path: '/sectors/restaurant/operations/order-tracking',
      icon: (
        <div className="bg-gradient-to-tl from-blue-600 to-indigo-400 shadow-soft-2xl mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5">
          <OrderIcon className="fill-white h-3 w-3" />
        </div>
      ),
      label: 'Sipariş Takibi'
    }
  ];

  // Franchise Owner menüsü - Business Owner ile aynı ama farklı permissions
  const franchiseOwnerMenuItems = [
    {
      type: 'single',
      path: '/brands',
      icon: (
        <div className="bg-gradient-to-tl from-purple-700 to-pink-500 shadow-soft-2xl mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5">
          <DashboardIcon className="fill-white h-3 w-3" />
        </div>
      ),
      label: 'İşletme Paneli'
    }
  ];

  // Kullanıcı rolüne göre menü seçimi
  const getMenuItems = () => {
    const currentPath = window.location.pathname;
    const businessSector = selectedBusiness?.sector || selectedBusiness?.business_type;
    console.log('Sidebar debug:', { sidebarType: contextSidebarType, businessSector, userRole, user, currentPath });

    // URL path kontrolü - Öncelikli
    // Business management routes - Always show business sidebar
    if (currentPath === '/subscription' || currentPath.startsWith('/subscription/') || 
        currentPath === '/brands' || currentPath.startsWith('/brands/') ||
        currentPath === '/dashboard/personal' || currentPath.startsWith('/dashboard/personal') ||
        contextSidebarType === 'business' || contextSidebarType === 'business_management') {
      console.log('🎯 Business management route detected, using business owner menu');
      return businessOwnerMenuItems;
    }

    // Admin routes - Always show admin sidebar
    if (currentPath.startsWith('/dashboard/admin')) {
      console.log('🎯 Admin route detected, using admin menu');
      return dynamicAdminMenuItems.length > 0 ? dynamicAdminMenuItems : adminMenuItems;
    }

    // Check for admin user type or system admin
    if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'system_admin' ||
      user?.user_type === 'system_admin' || user?.role === 'system_admin') {
      console.log('Using admin menu (system admin detected)');
      return dynamicAdminMenuItems.length > 0 ? dynamicAdminMenuItems : adminMenuItems;
    }

    switch (userRole) {
      case 'business_owner':
      case 'business_accounts':
      case 'human': // SaaS Unified user type
        // For restaurant businesses, use BusinessSidebar (handled in layout)
        if (user.permissions?.includes('franchise_management')) {
          console.log('Using franchise menu');
          return franchiseOwnerMenuItems;
        }
        console.log('Using business owner/human menu');
        return businessOwnerMenuItems;
      default:
        return employeeMenuItems;
    }
  };

  const menuItems = getMenuItems();

  // Login/Register sayfalarında sidebar gösterme
  const publicPages = ['/login', '/register', '/landing'];
  const isPublicPage = publicPages.includes(location.pathname);

  if (!isAuthenticated || isPublicPage) {
    return null;
  }

  if (user.userType === 'brand_employee') {
    console.log('Brand employee detected, should use BusinessSidebar (handled in layout)');
    return null;
  }

  return (
    <>
      {/* Desktop Sidebar - Static positioning */}
      <aside className={`relative h-screen ${collapsed ? 'w-16' : 'w-full lg:w-80'} overflow-hidden bg-slate-50 dark:bg-gray-800 border-r border-slate-200/60 dark:border-slate-700/50 p-0 antialiased flex flex-col transition-all duration-300 z-50`}>
        {/* Logo Header */}
        <div className={`h-19.5 flex-shrink-0 flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-6'} border-b border-slate-200/40 dark:border-slate-700/30 bg-white dark:bg-gray-800`}>
          {!collapsed && (
            <Link
              to={isAdminUser(user) ? '/dashboard/admin/' : (userRole === 'business_owner' || userRole === 'manager' || userRole === 'operator' || userRole === 'staff') ? '/brands' : '/sectors/restaurant/dashboard'}
              className="block py-6 m-0 text-sm whitespace-nowrap text-slate-700 dark:text-white flex-1"
            >
              <img
                src={darkMode ? sidebarLogoDark : sidebarLogoLight}
                className="inline h-full max-w-full transition-all duration-200 ease-nav-brand max-h-8"
                alt="logo"
              />
            </Link>
          )}

          {/* Desktop Collapse Button */}
          <button
            onClick={() => {
              const newCollapsed = !collapsed;
              setCollapsed(newCollapsed);
              if (onCollapse) onCollapse(newCollapsed);
            }}
            className="hidden lg:flex p-2 rounded-lg bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 text-slate-500 dark:text-gray-300 transition-colors cursor-pointer"
            title={collapsed ? "Genişlet" : "Daralt"}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <ChevronLeftIcon className="w-5 h-5" />
            )}
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={() => setSidebarOpen && setSidebarOpen(false)}
            className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors lg:hidden"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Time and Date */}
        {!collapsed && (
          <div className="px-6 py-2.5 border-b border-slate-200/40 dark:border-slate-700/20 bg-slate-50/50 dark:bg-gray-800/50 flex items-center justify-between text-[11px] font-bold text-slate-400 dark:text-gray-400">
            <div className="flex items-center space-x-1.5">
              <ClockIcon className="w-3.5 h-3.5 text-slate-400" />
              <span>{formatTime(currentTime)}</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <CalendarDaysIcon className="w-3.5 h-3.5 text-slate-400" />
              <span>{formatDate(currentTime)}</span>
            </div>
          </div>
        )}

        {/* Admin Impersonation Notice */}
        {!collapsed && user && user.isImpersonating && (
          <div className="px-6 py-2 bg-red-100 border-b border-red-200 dark:bg-red-900/20 dark:border-red-800">
            <div className="text-xs text-red-700 dark:text-red-300 text-center">
              <div className="font-semibold">Admin olarak giriş yapıldı</div>
              <button
                onClick={() => {
                  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                  const originalUserType = localStorage.getItem('originalUserType') || 'admin';

                  localStorage.setItem('user', JSON.stringify(adminUser));
                  localStorage.setItem('userType', originalUserType);
                  localStorage.removeItem('adminUser');
                  localStorage.removeItem('originalUserType');
                  localStorage.removeItem('selectedBusiness');

                  window.dispatchEvent(new Event('authStateChanged'));
                  window.location.href = '/dashboard/admin/users';
                }}
                className="mt-1 text-xs bg-red-600 hover:bg-red-500 px-2 py-1 rounded text-white"
              >
                Admin Paneline Dön
              </button>
            </div>
          </div>
        )}

        {/* Business Info Header */}
        {!collapsed && selectedBusiness && selectedBusiness.type !== 'business_account' && userRole !== 'admin' && userRole !== 'super_admin' && (
          <div className="px-6 py-3 border-b border-slate-200/40 bg-white dark:bg-gray-700 flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-blue-900 flex items-center justify-center text-lg flex-shrink-0">
              {selectedBusiness.business_type === 'restaurant' ? '🍽️' :
                selectedBusiness.business_type === 'retail' ? '🏪' :
                  selectedBusiness.business_type === 'service' ? '💄' : '🏢'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                {selectedBusiness.name}
              </div>
              <div className="text-xs text-slate-400 dark:text-gray-400">
                {selectedBusiness.business_type === 'restaurant' ? 'Restoran' :
                  selectedBusiness.business_type === 'retail' ? 'Perakende' :
                    selectedBusiness.business_type === 'service' ? 'Hizmet' : 'İşletme'} • Aktif
              </div>
            </div>
          </div>
        )}

        {/* Collapsed Business Icon */}
        {collapsed && selectedBusiness && selectedBusiness.type !== 'business_account' && userRole !== 'admin' && userRole !== 'super_admin' && (
          <div className="px-2 py-3 border-b border-slate-200/40 bg-white dark:bg-gray-700">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 dark:bg-blue-900 flex items-center justify-center text-lg mx-auto">
              {selectedBusiness.business_type === 'restaurant' ? '🍽️' :
                selectedBusiness.business_type === 'retail' ? '🏪' :
                  selectedBusiness.business_type === 'service' ? '💄' : '🏢'}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
          <nav className="space-y-1.5 px-3">
            {menuItems.map((item, index) => {
              if (item.type === 'single') {
                const isItemRouteActive = location.pathname.startsWith(item.path);
                const isActive = location.pathname === getWorkingRoute(item.path) || (item.path !== '/' && isItemRouteActive);
                return (
                  <Link
                    key={`single-${item.id || item.path}-${index}`}
                    to={getWorkingRoute(item.path)}
                    onClick={() => setSidebarOpen && setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200 group ${
                      isActive
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-100'
                        : 'text-slate-650 hover:bg-slate-200/50 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    title={collapsed ? item.label : ''}
                  >
                    <div className={`flex-shrink-0 ${collapsed ? 'mx-auto' : ''} ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-500 dark:text-gray-400'}`}>
                      {item.icon}
                    </div>
                    {!collapsed && (
                      <span className="font-semibold text-sm">{item.label}</span>
                    )}
                  </Link>
                );
              }

              if (item.type === 'group') {
                // If variant is v3 (Admin), show flat section headers
                if (item.variant === 'v3' && !collapsed) {
                  return (
                    <div key={`group-v3-${item.label || index}`} className="mt-4 w-full">
                      <div className="px-6 mb-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {item.label}
                        </span>
                      </div>
                      <nav className="space-y-1">
                        {item.children.map((child, cIdx) => {
                          const isChildActive = location.pathname.startsWith(child.path);
                          return (
                            <Link
                              key={`child-v3-${item.label || index}-${child.path || cIdx}-${cIdx}`}
                              to={getWorkingRoute(child.path)}
                              onClick={() => setSidebarOpen && setSidebarOpen(false)}
                              className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                                isChildActive
                                  ? 'bg-indigo-50 text-indigo-700 font-bold shadow-sm'
                                  : 'text-slate-600 hover:bg-slate-200/40 hover:text-slate-900'
                              }`}
                            >
                              <div className={`flex-shrink-0 ${isChildActive ? 'text-indigo-700' : 'text-slate-400 group-hover:text-slate-500'}`}>
                                {child.icon}
                              </div>
                              <span className="font-semibold text-sm">
                                {child.label}
                              </span>
                            </Link>
                          );
                        })}
                      </nav>
                    </div>
                  );
                }

                // Default behavior for other users or when collapsed
                const isExpanded = expandedGroups[item.id];
                return (
                  <div key={`group-${item.id}-${index}`}>
                    <button
                      onClick={() => toggleGroup(item.id)}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-slate-650 hover:bg-slate-200/50 hover:text-slate-900 dark:text-gray-300 dark:hover:bg-gray-700 transition-all duration-200 group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`flex-shrink-0 ${collapsed ? 'mx-auto' : ''} text-slate-400 group-hover:text-slate-500 dark:text-gray-400`}>
                          {item.icon}
                        </div>
                        {!collapsed && (
                          <span className="font-semibold text-sm">{item.label}</span>
                        )}
                      </div>
                      {!collapsed && (
                        <ChevronRightIcon
                          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      )}
                    </button>

                    {!collapsed && isExpanded && (
                      <div className="mt-1 ml-8 space-y-1">
                        {item.children && item.children.map((child, childIndex) => {
                          const isChildRouteActive = location.pathname.startsWith(child.path);
                          return (
                            <Link
                              key={`child-${item.id}-${child.path || child.id}-${childIndex}`}
                              to={getWorkingRoute(child.path)}
                              onClick={() => setSidebarOpen && setSidebarOpen(false)}
                              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                isChildRouteActive
                                  ? 'bg-slate-200/70 text-slate-950 font-bold'
                                  : 'text-slate-500 hover:bg-slate-200/30 hover:text-slate-900 dark:text-gray-400 dark:hover:bg-gray-700'
                              }`}
                            >
                              <div className={`flex-shrink-0 ${isChildRouteActive ? 'text-slate-950' : 'text-slate-400 group-hover:text-slate-500'}`}>
                                {child.icon}
                              </div>
                              <span>{child.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </nav>
        </div>


      </aside>
    </>
  );
};

export default Sidebar;
